import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { IndexerIcon } from '../components/IndexerIcon';
import {
  Button,
  Form,
  Spinner,
} from 'react-bootstrap';
import IconAlert from '@/components/common/IconAlert';
import { indexerAlertVariant } from '../lib/indexer-alert-variant';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useIndexerPaths } from '../lib/indexer-paths';
import {
  getIndexerErrorInfo,
  invalidUrlsFromError,
  jobIdFromError,
} from '../api/errors';
import {
  useCancelProjectJobMutation,
  useCheckRecentIndexUrlsMutation,
  useCheckUrlsHeadMutation,
  useEnqueueIndexMutation,
  useLazyGetJobRetryUrlsQuery,
  useSubmitIndexNowMutation,
} from '../api/indexerApi';
import type { NotificationType, ProjectJob, RecentIndexData, UrlHeadCheckResult } from '../api/types';
import {
  runBrowserHeadChecks,
  shouldFallbackHeadToServer,
} from '../lib/browser-head-check';
import { ActionTip } from '../components/ActionTip';
import { HeadCheckHelpTip } from '../components/HeadCheckHelpTip';
import { IndexDeleteConfirmModal } from '../components/IndexDeleteConfirmModal';
import { IndexLimitHelpTip } from '../components/IndexLimitHelpTip';
import { IndexQuotaHelpTip } from '../components/IndexQuotaHelpTip';
import { IndexSchedulePanel } from '../components/IndexSchedulePanel';
import { JobResultsPanel } from '../components/JobResultsPanel';
import { NeedProject } from '../components/NeedProject';
import { IndexerChartCard } from '../components/IndexerChartCard';
import { WorkspacePagePreload } from '../components/preloads/IndexerPreloads';
import { RecentIndexWarningModal } from '../components/RecentIndexWarningModal';
import { SitemapUrlImport } from '../components/SitemapUrlImport';
import { UrlListField } from '../components/UrlListField';
import { UrlHeadCheckModal } from '../components/UrlHeadCheckModal';
import {
  RequestHistory,
} from '../components/RequestHistory';
import { StatsCards, GOOGLE_DAILY_QUOTA } from '../components/StatsCards';
import { IndexPageCharts } from '../components/charts/IndexPageCharts';
import {
  WorkspaceModeSwitch,
  type WorkspaceMode,
} from '../components/WorkspaceModeSwitch';
import { useProject } from '../lib/project-context';
import { useJobHistory } from '../lib/use-job-history';
import { googleIndexingUsedToday, jobsForPageStats, STATS_RANGE_OPTIONS, type StatsRange } from '../lib/job-range';
import { formatFaNumber, mergeUrlLists, parseUrlList, removeUrlsFromText, validateProjectUrlList } from '../lib/utils';
import {
  readRouteUrlPrefill,
  stashIndexUrlPrefill,
  takeIndexUrlPrefill,
} from '../lib/url-list-prefill';

const INDEX_LIMIT = 200;
const DELETE_LIMIT = 1000;
const INDEX_JOB_KINDS = ['google_index', 'indexnow'] as const;

type EngineSelection = { google: boolean; indexnow: boolean };

const DEFAULT_ENGINES: EngineSelection = { google: true, indexnow: false };
const INDEXNOW_SELECTABLE = true;

type PageTab = WorkspaceMode;

function indexPrefillMessage(source?: string, count?: number): string {
  if (source === 'inspect-history' || source === 'inspect-results') {
    if (count != null && count > 1) {
      return `${formatFaNumber(count)} آدرس ایندکس‌نشده در فرم درخواست ایندکس قرار گرفت. در صورت نیاز ارسال کنید.`;
    }
    return 'آدرس از وضعیت ایندکس در فرم قرار گرفت. در صورت نیاز ارسال کنید.';
  }
  return 'آدرس در فرم درخواست ایندکس قرار گرفت. در صورت نیاز ارسال کنید.';
}

export function IndexPage() {
  const { project, projectRef } = useProject();
  const [enqueueIndex] = useEnqueueIndexMutation();
  const [submitIndexNow] = useSubmitIndexNowMutation();
  const [checkRecentIndexUrls] = useCheckRecentIndexUrlsMutation();
  const [cancelProjectJob] = useCancelProjectJobMutation();
  const [checkUrlsHead] = useCheckUrlsHeadMutation();
  const [getJobRetryUrls] = useLazyGetJobRetryUrlsQuery();
  const location = useLocation();
  const navigate = useNavigate();
  const paths = useIndexerPaths();
  const [engines, setEngines] = useState<EngineSelection>(DEFAULT_ENGINES);
  const [pageTab, setPageTab] = useState<PageTab>('charts');
  const [urlText, setUrlText] = useState('');
  const [type, setType] = useState<NotificationType>('URL_UPDATED');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ variant: string; text: string } | null>(
    null,
  );
  const [recentWarning, setRecentWarning] = useState<RecentIndexData | null>(
    null,
  );
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [headCheckOpen, setHeadCheckOpen] = useState(false);
  const [headCheckRunning, setHeadCheckRunning] = useState(false);
  const [headCheckUrls, setHeadCheckUrls] = useState<string[]>([]);
  const [headCheckResults, setHeadCheckResults] = useState<
    Record<string, UrlHeadCheckResult | undefined>
  >({});
  const headCheckAbortRef = useRef<AbortController | null>(null);
  const formActionsRef = useRef<HTMLDivElement>(null);
  const [rangeDays, setRangeDays] = useState<StatsRange>(7);
  const [formError, setFormError] = useState<string | null>(null);
  const [retryingJobId, setRetryingJobId] = useState<string | null>(null);
  const [stoppingJobId, setStoppingJobId] = useState<string | null>(null);

  const urls = useMemo(() => parseUrlList(urlText), [urlText]);
  const isDeleteRequest = type === 'URL_DELETED';
  const limit = isDeleteRequest ? DELETE_LIMIT : INDEX_LIMIT;
  const effectiveIndexNow = INDEXNOW_SELECTABLE && engines.indexnow;
  const indexNowConfigured = Boolean(
    project?.indexNowKey && project?.indexNowKeyFileUrl,
  );
  const indexNowNeedsSetup = effectiveIndexNow && !indexNowConfigured;
  const submitValidationError = useMemo(() => {
    if (!project) return null;
    if (!engines.google && !effectiveIndexNow) {
      return 'حداقل یک موتور را انتخاب کنید.';
    }
    if (indexNowNeedsSetup) {
      return 'برای ارسال به Bing و Yandex، ابتدا کلید را از تنظیمات بسازید.';
    }
    return validateProjectUrlList(urls, project, limit);
  }, [
    urls,
    project,
    limit,
    engines.google,
    effectiveIndexNow,
    indexNowNeedsSetup,
  ]);
  const headCheckValidationError = useMemo(
    () => (project ? validateProjectUrlList(urls, project, INDEX_LIMIT) : null),
    [urls, project],
  );

  const history = useJobHistory(projectRef ?? undefined, [...INDEX_JOB_KINDS]);
  const rangedJobs = useMemo(
    () => jobsForPageStats(history.jobs, rangeDays, pageTab),
    [history.jobs, rangeDays, pageTab],
  );
  const todayGoogleQuotaUsed = useMemo(
    () => googleIndexingUsedToday(history.jobs),
    [history.jobs],
  );

  const prefillAppliedRef = useRef(false);

  useLayoutEffect(() => {
    const fromStorage = takeIndexUrlPrefill();
    const fromRoute = fromStorage ? null : readRouteUrlPrefill(location.state);
    if (fromRoute) {
      stashIndexUrlPrefill(fromRoute.urls, fromRoute.source);
      navigate('.', { replace: true, state: null });
    }

    const payload = fromStorage ?? fromRoute;
    if (!payload || prefillAppliedRef.current) return;

    prefillAppliedRef.current = true;
    setUrlText(payload.urls.join('\n'));
    setPageTab('manual');
    setEngines(DEFAULT_ENGINES);
    setType('URL_UPDATED');
    setMessage({
      variant: 'info',
      text: indexPrefillMessage(payload.source, payload.urls.length),
    });
  }, [location.state, location.key, navigate, project?.id]);

  useEffect(() => {
    prefillAppliedRef.current = false;
  }, [location.key]);

  useEffect(() => {
    setFormError(null);
  }, [urlText, engines, type]);

  const showJobResults = Boolean(
    history.selectedId && pageTab === 'charts',
  );

  function onImportFromSitemap(selected: string[]) {
    if (selected.length === 0) return;
    setUrlText(mergeUrlLists(urls, selected, limit).join('\n'));
    setMessage({
      variant: 'success',
      text: `${selected.length} آدرس از نقشه سایت به لیست اضافه شد.`,
    });
  }

  async function sendUrls(
    targetUrls: string[],
    options?: { engines?: EngineSelection; type?: NotificationType },
  ) {
    if (!project || !projectRef) return;
    const useEngines = options?.engines ?? engines;
    const useType = options?.type ?? type;
    const useGoogle = useEngines.google;
    const useIndexNow =
      INDEXNOW_SELECTABLE && useEngines.indexnow && useType !== 'URL_DELETED';

    if (!useGoogle && !useIndexNow) {
      if (useType === 'URL_DELETED' && useEngines.indexnow && !useGoogle) {
        showFormValidationError(
          'حذف از نتایج برای سایر (Bing / Yandex) به‌زودی اضافه می‌شود؛ فعلاً گوگل را انتخاب کنید.',
        );
      } else {
        showFormValidationError('حداقل یک موتور را انتخاب کنید.');
      }
      return;
    }

    if (
      useIndexNow &&
      !(project.indexNowKey && project.indexNowKeyFileUrl)
    ) {
      showFormValidationError(
        'برای ارسال به Bing و Yandex، ابتدا کلید را از تنظیمات بسازید.',
      );
      return;
    }

    setBusy(true);
    const parts: string[] = [];
    let openJobId: string | undefined;
    let hadError = false;

    try {
      if (useGoogle) {
        const queued = await enqueueIndex({
          projectId: projectRef,
          urls: targetUrls,
          type: useType,
        }).unwrap();
        parts.push(`گوگل: ${queued.total} آدرس در صف`);
        openJobId = queued.jobId;
      }

      if (useIndexNow) {
        const result = await submitIndexNow({
          projectId: projectRef,
          urls: targetUrls,
        }).unwrap();
        parts.push(
          result.success
            ? `سایر: ${result.indexNow.messageFa}`
            : `سایر: HTTP ${result.httpStatus} — ${result.indexNow.messageFa}`,
        );
        if (!result.success) hadError = true;
        openJobId = result.jobId;
      }

      setMessage({
        variant: hadError ? 'danger' : 'info',
        text: parts.join(' · '),
      });
      if (openJobId) {
        setPageTab('charts');
        await history.openJob(openJobId);
      }
    } catch (err) {
      const info = getIndexerErrorInfo(err);
      const invalid = invalidUrlsFromError(err);
      const httpPrefix = info.code ? `HTTP ${info.code} — ` : '';
      setMessage({
        variant: 'danger',
        text:
          invalid.length > 0
            ? `${httpPrefix}${info.message} — نامعتبر: ${invalid.join(', ')}`
            : `${httpPrefix}${info.message}`,
      });
      const failedJobId = jobIdFromError(err);
      if (failedJobId) {
        setPageTab('charts');
        await history.openJob(failedJobId);
      }
    } finally {
      setBusy(false);
    }
  }

  async function onStopJob(job: ProjectJob) {
    if (!project || !projectRef || stoppingJobId) return;
    setMessage(null);
    setStoppingJobId(job.id);
    try {
      const updated = await cancelProjectJob({
        projectId: projectRef,
        jobId: job.id,
      }).unwrap();
      setMessage({
        variant: 'info',
        text:
          updated.state === 'cancelled'
            ? 'درخواست متوقف شد. آدرس‌های باقی‌مانده اجرا نمی‌شوند.'
            : 'توقف ثبت شد. آدرس جاری تمام می‌شود و بقیه اجرا نمی‌شوند.',
      });
      await history.refresh();
      await history.refreshSelected();
    } catch (err) {
      setMessage({
        variant: 'danger',
        text: getIndexerErrorInfo(err).message || 'توقف درخواست ناموفق بود',
      });
    } finally {
      setStoppingJobId(null);
    }
  }

  async function onRetryJob(job: ProjectJob) {
    if (!project || !projectRef || retryingJobId) return;
    setMessage(null);
    setFormError(null);
    setRetryingJobId(job.id);
    try {
      const plan = await getJobRetryUrls(
        { projectId: projectRef, jobId: job.id },
        true,
      ).unwrap();
      const targetUrls = plan.urls;
      if (targetUrls.length === 0) {
        setMessage({
          variant: 'danger',
          text:
            plan.skippedSucceededElsewhere > 0
              ? 'آدرس باقی‌مانده‌ای نیست؛ موارد موفق در تلاش‌های بعدی همین درخواست دیگر دوباره بررسی نمی‌شوند.'
              : 'آدرس باقی‌مانده یا ناموفقی برای تلاش مجدد در این درخواست نیست.',
        });
        return;
      }
      const jobEngine = job.kind === 'indexnow' ? 'indexnow' : 'google';
      const jobType: NotificationType =
        job.type === 'URL_DELETED' ? 'URL_DELETED' : 'URL_UPDATED';
      setEngines({
        google: jobEngine === 'google',
        indexnow: jobEngine === 'indexnow',
      });
      setType(jobType);
      if (plan.skippedSucceededElsewhere > 0) {
        setMessage({
          variant: 'info',
          text: `${formatFaNumber(plan.skippedSucceededElsewhere)} آدرس موفق قبلی رد شد؛ تلاش مجدد برای ${formatFaNumber(targetUrls.length)} آدرس.`,
        });
      }
      await sendUrls(targetUrls, {
        engines: {
          google: jobEngine === 'google',
          indexnow: jobEngine === 'indexnow',
        },
        type: jobType,
      });
    } catch (err) {
      setMessage({
        variant: 'danger',
        text: getIndexerErrorInfo(err).message || 'تلاش مجدد ناموفق بود',
      });
    } finally {
      setRetryingJobId(null);
    }
  }

  function showFormValidationError(text: string) {
    setMessage(null);
    setFormError(text);
    requestAnimationFrame(() => {
      formActionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }

  async function onHeadCheck() {
    if (!project || !projectRef) return;
    setMessage(null);
    if (urls.length === 0) {
      showFormValidationError('حداقل یک URL وارد کنید.');
      return;
    }
    if (headCheckValidationError) {
      showFormValidationError(headCheckValidationError);
      return;
    }

    headCheckAbortRef.current?.abort();
    const controller = new AbortController();
    headCheckAbortRef.current = controller;

    const list = [...urls];
    setHeadCheckUrls(list);
    setHeadCheckResults({});
    setHeadCheckOpen(true);
    setHeadCheckRunning(true);

    try {
      const browserResults = await runBrowserHeadChecks(list, {
        signal: controller.signal,
        onResult: (item) => {
          // CORS/شبکه را هنوز قطعی نکن — تا fallback بک‌اند ردیف «در انتظار» بماند
          if (shouldFallbackHeadToServer(item)) return;
          setHeadCheckResults((prev) => ({ ...prev, [item.url]: item }));
        },
      });

      if (controller.signal.aborted) return;

      const fallbackUrls = browserResults
        .filter(shouldFallbackHeadToServer)
        .map((item) => item.url);

      if (fallbackUrls.length === 0) return;

      try {
        const data = await checkUrlsHead({
          projectId: projectRef,
          urls: fallbackUrls,
        }).unwrap();
        if (controller.signal.aborted) return;

        const byUrl = new Map(data.results.map((item) => [item.url, item]));
        setHeadCheckResults((prev) => {
          const next = { ...prev };
          for (const url of fallbackUrls) {
            next[url] = byUrl.get(url) ?? {
              url,
              status: null,
              ok: false,
              error: 'network',
            };
          }
          return next;
        });
      } catch {
        if (controller.signal.aborted) return;
        setHeadCheckResults((prev) => {
          const next = { ...prev };
          for (const url of fallbackUrls) {
            next[url] = { url, status: null, ok: false, error: 'network' };
          }
          return next;
        });
      }
    } finally {
      if (headCheckAbortRef.current === controller) {
        headCheckAbortRef.current = null;
      }
      if (!controller.signal.aborted) {
        setHeadCheckRunning(false);
      }
    }
  }

  function closeHeadCheckModal() {
    headCheckAbortRef.current?.abort();
    headCheckAbortRef.current = null;
    setHeadCheckOpen(false);
    setHeadCheckRunning(false);
  }

  async function onSubmit() {
    if (!project || !projectRef) return;
    setMessage(null);
    if (submitValidationError) {
      showFormValidationError(submitValidationError);
      return;
    }

    if (isDeleteRequest) {
      if (!engines.google) {
        showFormValidationError(
          engines.indexnow
            ? 'حذف از نتایج برای سایر (Bing / Yandex) به‌زودی اضافه می‌شود؛ فعلاً گوگل را انتخاب کنید.'
            : 'برای حذف از نتایج، موتور گوگل را انتخاب کنید.',
        );
        return;
      }
      setDeleteConfirmOpen(true);
      return;
    }

    if (engines.google && type === 'URL_UPDATED') {
      setBusy(true);
      try {
        const recent = await checkRecentIndexUrls({
          projectId: projectRef,
          urls,
        }).unwrap();
        if (recent.urls.length > 0) {
          setRecentWarning(recent);
          return;
        }
      } catch (err) {
        setMessage({
          variant: 'danger',
          text:
            getIndexerErrorInfo(err).message ||
            'بررسی آدرس‌های تکراری ناموفق بود',
        });
        return;
      } finally {
        setBusy(false);
      }
    }

    await sendUrls(urls);
  }

  function onConfirmDelete() {
    setDeleteConfirmOpen(false);
    void sendUrls(urls);
  }

  function onRemoveRecent() {
    if (!recentWarning) return;
    const dropped = recentWarning.urls.map((item) => item.url);
    const nextText = removeUrlsFromText(urlText, dropped);
    const remaining = parseUrlList(nextText);
    setUrlText(nextText);
    setRecentWarning(null);
    if (remaining.length === 0) {
      setMessage({
        variant: 'info',
        text: 'آدرس‌های تکراری از لیست حذف شدند.',
      });
      return;
    }
    void sendUrls(remaining);
  }

  function onContinueRecent() {
    setRecentWarning(null);
    void sendUrls(urls);
  }

  if (!project || !projectRef) {
    return <NeedProject />;
  }

  return (
    <div className="d-flex flex-column gap-3">
      {message && (
        <IconAlert
          variant={indexerAlertVariant(message.variant)}
          dismissible
          onClose={() => setMessage(null)}
        >
          <p className="mb-0 text-justify pe-3">{message.text}</p>
        </IconAlert>
      )}

      <IndexDeleteConfirmModal
        show={deleteConfirmOpen}
        urlCount={urls.length}
        busy={busy}
        onConfirm={onConfirmDelete}
        onClose={() => setDeleteConfirmOpen(false)}
      />

      <RecentIndexWarningModal
        show={recentWarning !== null}
        windowHours={recentWarning?.windowHours ?? 24}
        urls={recentWarning?.urls ?? []}
        onRemove={onRemoveRecent}
        onContinue={onContinueRecent}
        onClose={() => setRecentWarning(null)}
      />

      <UrlHeadCheckModal
        show={headCheckOpen}
        running={headCheckRunning}
        urls={headCheckUrls}
        results={headCheckResults}
        onClose={closeHeadCheckModal}
      />

      <WorkspaceModeSwitch
        value={pageTab}
        onChange={setPageTab}
        context="index"
        aside={
          <Form.Select
            size="sm"
            className="w-auto"
            value={rangeDays}
            onChange={(e) =>
              setRangeDays(Number(e.target.value) as StatsRange)
            }
            aria-label="بازه زمانی آمار"
          >
            {STATS_RANGE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        }
      />

      {history.loadingList && history.jobs.length === 0 ? (
        <WorkspacePagePreload />
      ) : (
        <>
      <StatsCards
        jobs={rangedJobs}
        rangeDays={rangeDays}
        onRangeChange={setRangeDays}
        showRangeSelect={false}
        dailyQuota={{
          used: todayGoogleQuotaUsed,
          limit: GOOGLE_DAILY_QUOTA,
          label: 'درخواست ایندکس',
          help: <IndexQuotaHelpTip />,
        }}
      />

      <div>
        {pageTab === 'manual' ? (
          <IndexerChartCard
            className="mb-0"
            title="ارسال دستی"
            subtitle="لیست URL یا sitemap را برای ایندکس همین الان بفرستید"
          >
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                <Form.Label className="mb-0 d-inline-flex align-items-center gap-1 flex-wrap">
                  <span>
                    لیست آدرس‌ها (حداکثر {limit}
                    {limit === INDEX_LIMIT && (
                      <>
                        {' '}
                        <IndexLimitHelpTip />
                      </>
                    )}
                    )
                  </span>
                </Form.Label>
                <SitemapUrlImport
                  projectId={projectRef}
                  project={project}
                  limit={limit}
                  existingUrls={urls}
                  onImport={onImportFromSitemap}
                />
              </div>
              <UrlListField
                project={project}
                value={urlText}
                limit={limit}
                onChange={setUrlText}
              />

              {formError && (
                <IconAlert
                  variant="danger"
                  className="mt-3 mb-0"
                  dismissible
                  onClose={() => setFormError(null)}
                >
                  <p className="mb-0 text-justify pe-3">{formError}</p>
                </IconAlert>
              )}

              <div ref={formActionsRef} className="pt-2">
                <div className="mb-3">
                  <Form.Label className="mb-2">موتورهای ایندکس</Form.Label>
                  <div className="d-flex flex-wrap gap-3">
                    <Form.Check
                      type="checkbox"
                      id="engine-google"
                      label={
                        <span className="d-inline-flex align-items-center gap-1">
                          <IndexerIcon name="google" className="flex-shrink-0" />
                          گوگل
                        </span>
                      }
                      checked={engines.google}
                      onChange={(e) =>
                        setEngines((prev) => ({ ...prev, google: e.target.checked }))
                      }
                    />
                    {!INDEXNOW_SELECTABLE ? (
                      <ActionTip tip="به‌زودی" delay={{ show: 0, hide: 100 }}>
                        <span className="d-inline-block">
                          <Form.Check
                            type="checkbox"
                            id="engine-indexnow"
                            label={
                              <span className="d-inline-flex align-items-center gap-1 text-secondary">
                                <IndexerIcon name="globe2" className="flex-shrink-0" />
                                سایر (Bing / Yandex)
                              </span>
                            }
                            checked={false}
                            disabled
                            onChange={() => undefined}
                          />
                        </span>
                      </ActionTip>
                    ) : (
                      <Form.Check
                        type="checkbox"
                        id="engine-indexnow"
                        label={
                          <span className="d-inline-flex align-items-center gap-1">
                            <IndexerIcon name="globe2" className="flex-shrink-0" />
                            سایر (Bing / Yandex)
                          </span>
                        }
                        checked={engines.indexnow}
                        onChange={(e) =>
                          setEngines((prev) => ({ ...prev, indexnow: e.target.checked }))
                        }
                      />
                    )}
                  </div>
                  {indexNowNeedsSetup && (
                    <IconAlert variant="warning" className="mt-3 mb-0">
                      <p className="mb-0 text-justify pe-3">
                        برای ارسال به Bing و Yandex، ابتدا کلید را در{' '}
                        <Link to={paths.settings}>تنظیمات</Link> بسازید.
                      </p>
                    </IconAlert>
                  )}
                </div>

                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <Form.Select
                    className="fs--1"
                    style={{ maxWidth: 180 }}
                    value={type}
                    onChange={(e) => setType(e.target.value as NotificationType)}
                  >
                    <option value="URL_UPDATED">درخواست ایندکس</option>
                    <option value="URL_DELETED" disabled>
                      حذف از نتایج — به‌زودی
                    </option>
                  </Form.Select>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <Button
                      variant="outline-secondary"
                      className={`d-inline-flex align-items-center gap-2${
                        busy || headCheckRunning || urls.length === 0
                          ? ' disabled'
                          : ''
                      }`}
                      aria-disabled={
                        busy || headCheckRunning || urls.length === 0
                      }
                      onClick={() => {
                        if (busy || headCheckRunning || urls.length === 0) {
                          return;
                        }
                        void onHeadCheck();
                      }}
                    >
                      {headCheckRunning && (
                        <Spinner
                          size="sm"
                          animation="border"
                          style={{ width: 12, height: 12 }}
                        />
                      )}
                      تست ۲۰۰
                      <span
                        className="d-inline-flex"
                        onClick={(event) => event.stopPropagation()}
                        onMouseDown={(event) => event.stopPropagation()}
                      >
                        <HeadCheckHelpTip />
                      </span>
                    </Button>
                    <Button
                      type="button"
                      variant={isDeleteRequest ? 'danger' : 'falcon-primary'}
                      disabled={busy || indexNowNeedsSetup}
                      onClick={() => void onSubmit()}
                    >
                      {busy && (
                        <Spinner
                          size="sm"
                          animation="border"
                          className="me-2"
                          style={{ width: 12, height: 12 }}
                        />
                      )}
                      {isDeleteRequest ? 'حذف' : 'ارسال برای ایندکس'}
                    </Button>
                  </div>
                </div>
              </div>
          </IndexerChartCard>
        ) : pageTab === 'schedule' ? (
          <IndexerChartCard
            className="mb-0"
            title="زمان‌بندی"
            subtitle="خواندن دوره‌ای sitemap و ارسال آدرس‌های تازه برای ایندکس"
          >
            <IndexSchedulePanel projectId={projectRef} />
          </IndexerChartCard>
        ) : (
          <IndexPageCharts jobs={rangedJobs} rangeDays={rangeDays} />
        )}
      </div>

      {pageTab === 'charts' && (
        <div>
          <RequestHistory
            title="تاریخچه"
            jobs={history.jobs}
            trigger="all"
            selectedId={history.selectedId}
            onSelect={history.select}
            loading={history.loadingList}
            onRetry={(job) => void onRetryJob(job)}
            retryingJobId={retryingJobId}
            onStop={(job) => void onStopJob(job)}
            stoppingJobId={stoppingJobId}
            indexBulkActions={{
              projectId: projectRef,
              onNotify: (next) => setMessage(next),
            }}
          />
        </div>
      )}
        </>
      )}

      <JobResultsPanel
        show={showJobResults}
        projectId={projectRef}
        job={history.selectedJob}
        loading={history.loadingJob}
        onClose={history.close}
        onNotify={(next) => setMessage(next)}
        onRetry={(job) => void onRetryJob(job)}
        retryBusy={!!retryingJobId}
        onStop={(job) => void onStopJob(job)}
        stopBusy={!!stoppingJobId}
        onLiveProgress={history.applyLiveProgress}
        onLiveRefresh={() => {
          void history.refreshSelected();
          void history.refresh();
        }}
      />
    </div>
  );
}
